import { useId } from 'react';
import { FileText, X } from 'lucide-react';
import { Button, Input, Label, Spinner } from '@design-system';
import { DocumentDropzone } from '../components/document-dropzone';
import { StepFrame } from '../components/step-frame';
import { useConsumerFlow } from '../hooks/use-consumer-flow';
import { useIsPolicyReviewIncluded, usePolicyReviewUpload } from '../hooks/use-policy-review';
import { usePolicyReviewDraft, usePolicyReviewDraftActions } from '../stores/policy-review-draft-store';
import type { ConsumerStepProps } from './step-props';

export function ReviewUploadStep({ clientId }: ConsumerStepProps) {
  const { goTo, back } = useConsumerFlow(clientId);
  const { reviewType, documents, responseDeadline } = usePolicyReviewDraft();
  const { removeDocument, setResponseDeadline } = usePolicyReviewDraftActions();
  const { uploadFiles, isUploading, pendingNames, error } = usePolicyReviewUpload(clientId);
  const isIncluded = useIsPolicyReviewIncluded(clientId);
  const deadlineId = useId();

  return (
    <StepFrame
      step="review-upload"
      title="Add your policy documents."
      subtitle="You can add several files, including statements, riders and amendments."
      onBack={() => back('existing-policy')}
      help={
        <>
          <p>Include the full policy and its riders, plus your latest benefit or annual statement.</p>
          {reviewType === 'rate' && (
            <p>Also include every page of the rate-increase letter, reduced-benefit offers, election forms and response deadline. Our review doesn’t extend the carrier’s deadline or make an election for you.</p>
          )}
          <p>The turnaround begins once we have the complete documents. We’ll contact you if anything is missing.</p>
        </>
      }
      primary={{
        label: 'Continue',
        onClick: () => goTo('review-checkout'),
        disabled: documents.length === 0 || isUploading,
      }}
      error={error}
      next={isIncluded ? 'review and send your request.' : 'review the one-time analysis fee.'}
    >
      <DocumentDropzone accept=".pdf,.png,.jpg,.jpeg" isUploading={isUploading} onFiles={uploadFiles} />

      <ul className="flex flex-col gap-2" data-testid="consumer-review-documents">
        {documents.length === 0 && pendingNames.length === 0 && (
          <li className="text-muted-foreground text-sm">No documents added yet. You can add more files whenever you need.</li>
        )}
        {documents.map(document => (
          <li key={document.key} className="flex items-center justify-between gap-3 rounded-lg border bg-white px-3 py-2 text-sm">
            <span className="flex min-w-0 items-center gap-2">
              <FileText className="size-4 shrink-0" />
              <span className="truncate">{document.name}</span>
            </span>
            <Button variant="ghost" size="icon" aria-label={`Remove ${document.name}`} onClick={() => removeDocument(document.key)}>
              <X />
            </Button>
          </li>
        ))}
        {pendingNames.map(name => (
          <li
            key={name}
            className="text-muted-foreground animate-in fade-in flex items-center gap-2 rounded-lg border border-dashed bg-white px-3 py-2.5 text-sm duration-200 ease-out motion-reduce:animate-none"
          >
            <Spinner className="size-4 shrink-0" />
            <span className="truncate">Uploading {name}…</span>
          </li>
        ))}
      </ul>

      {reviewType === 'rate' && (
        <div className="flex flex-col gap-2">
          <Label htmlFor={deadlineId}>Carrier response deadline, if shown (optional)</Label>
          <Input
            id={deadlineId}
            type="date"
            className="bg-white"
            value={responseDeadline}
            onChange={event => setResponseDeadline(event.target.value)}
          />
        </div>
      )}
    </StepFrame>
  );
}
